import { Component, OnInit } from '@angular/core';
import {
  DeviceTypeListTableComponent,
  DeviceTypeViewModel,
} from './device-type-list-table/device-type-list-table.component';
import { DeviceTypeService } from '@services/api/device-type.service';
import { MatDialog } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import {
  DeviceTypeEditDialogComponent,
  DeviceTypeEditorDialogViewModel,
} from './device-type-edit-dialog/device-type-edit-dialog.component';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { firstValueFrom } from 'rxjs';
import {
  DeviceTypeAddRequest,
  DeviceTypeUpdateRequest,
} from '@entities/deviceType';

@Component({
  selector: 'app-device-type-list',
  standalone: true,
  imports: [DeviceTypeListTableComponent, MatCardModule],
  templateUrl: './device-type-list.component.html',
  styleUrl: './device-type-list.component.scss',
})
export class DeviceTypeListComponent implements OnInit {
  /** デバイス種類一覧 */
  public deviceTypeList: DeviceTypeViewModel[] = [];

  constructor(
    private deviceTypeService: DeviceTypeService,
    private dialog: MatDialog,
  ) {}

  public ngOnInit(): void {
    // デバイス種類一覧を取得する
    this._initDeviceTypeList();
  }

  /**
   * デバイス種類追加/編集ダイアログを表示する
   * @param deviceType デバイス種類
   */
  public async openDeviceTypeEditDialog(deviceType?: DeviceTypeViewModel) {
    const options: DeviceTypeEditorDialogViewModel = {
      updating: false,
    };

    if (deviceType) {
      options.updating = true;
      options.target = {
        deviceTypeKey: deviceType.deviceTypeKey,
        deviceTypeId: deviceType.deviceTypeId,
        displayName: deviceType.displayName,
        description: deviceType.description,
        samplingFrequency: deviceType.samplingFrequency,
        reportingFrequency: deviceType.reportingFrequency,
        temperatureSensor: deviceType.temperatureSensor,
        pressureSensor: deviceType.pressureSensor,
        humiditySensor: deviceType.humiditySensor,
        tiltSensor: deviceType.tiltSensor,
        lightSensor: deviceType.lightSensor,
        locationSensor: deviceType.locationSensor,
        locationTechGnss: deviceType.locationTechGnss,
        locationTechWifi: deviceType.locationTechWifi,
        locationTechCellular: deviceType.locationTechCellular,
        fallDetected: deviceType.fallDetected,
        mcdDetected: deviceType.mcdDetected,
        lightExposureDetected: deviceType.lightExposureDetected,
        shockDetected: deviceType.shockDetected,
        fmd: deviceType.fmd,
      };
    }

    const editedDeviceType: DeviceTypeViewModel = await firstValueFrom(
      this.dialog
        .open(DeviceTypeEditDialogComponent, {
          width: '90%',
          maxWidth: '1000px',
          data: options,
        })
        .afterClosed(),
    );

    if (!editedDeviceType) {
      return;
    }

    if (options.updating) {
      // デバイス種類更新処理
      const updatedDeviceTypeRequest: DeviceTypeUpdateRequest = {
        deviceTypeKey: editedDeviceType.deviceTypeKey,
        deviceTypeId: editedDeviceType.deviceTypeId,
        displayName: editedDeviceType.displayName,
        description: editedDeviceType.description,
        samplingFrequency: editedDeviceType.samplingFrequency,
        reportingFrequency: editedDeviceType.reportingFrequency,
        temperatureSensor: editedDeviceType.temperatureSensor,
        pressureSensor: editedDeviceType.pressureSensor,
        humiditySensor: editedDeviceType.humiditySensor,
        tiltSensor: editedDeviceType.tiltSensor,
        lightSensor: editedDeviceType.lightSensor,
        locationSensor: editedDeviceType.locationSensor,
        locationTechGnss: editedDeviceType.locationTechGnss,
        locationTechWifi: editedDeviceType.locationTechWifi,
        locationTechCellular: editedDeviceType.locationTechCellular,
        fallDetected: editedDeviceType.fallDetected,
        mcdDetected: editedDeviceType.mcdDetected,
        lightExposureDetected: editedDeviceType.lightExposureDetected,
        shockDetected: editedDeviceType.shockDetected,
        fmd: editedDeviceType.fmd,
      };
      this.deviceTypeService.update(updatedDeviceTypeRequest).subscribe({
        next: () => {
          // デバイス種類一覧を再取得する
          this._initDeviceTypeList();
        },
        error: (error) => {
          let message = '';
          if (
            error.response.status == 400 &&
            error.response.error.detail.type == 'NotFoundWebException'
          ) {
            message =
              '他のユーザーによってデータが変更されたため、更新に失敗しました。最新のデータを取得して再度試してください。';
          } else {
            message =
              'デバイス種類の変更に失敗しました。\nしばらくしてからもう一度お試しください。';
          }
          const alertDialogOptions: AlertDialogOption = {
            type: 'error',
            message: message,
          };
          this.dialog.open(AlertDialogComponent, {
            width: '600px',
            data: alertDialogOptions,
          });
        },
      });
    } else {
      // デバイス種類追加処理
      const addedDeviceTypeRequest: DeviceTypeAddRequest = {
        deviceTypeId: editedDeviceType.deviceTypeId,
        displayName: editedDeviceType.displayName,
        description: editedDeviceType.description,
        // csv系列のテキストデータから数字の配列にしてデータを送信
        samplingFrequency: editedDeviceType.samplingFrequency,
        reportingFrequency: editedDeviceType.reportingFrequency,
        temperatureSensor: editedDeviceType.temperatureSensor,
        pressureSensor: editedDeviceType.pressureSensor,
        humiditySensor: editedDeviceType.humiditySensor,
        tiltSensor: editedDeviceType.tiltSensor,
        lightSensor: editedDeviceType.lightSensor,
        locationSensor: editedDeviceType.locationSensor,
        locationTechGnss: editedDeviceType.locationTechGnss,
        locationTechWifi: editedDeviceType.locationTechWifi,
        locationTechCellular: editedDeviceType.locationTechCellular,
        fallDetected: editedDeviceType.fallDetected,
        mcdDetected: editedDeviceType.mcdDetected,
        lightExposureDetected: editedDeviceType.lightExposureDetected,
        shockDetected: editedDeviceType.shockDetected,
        fmd: editedDeviceType.fmd,
      };

      this.deviceTypeService.add(addedDeviceTypeRequest).subscribe({
        next: () => {
          // デバイス種類一覧を再取得する
          this._initDeviceTypeList();
        },
        error: (error) => {
          let message = '';
          if (error.response.error.detail.type == 'AlreadyExistsWebException') {
            message = `デバイス種類が重複しているため、追加に失敗しました。\n別の名前でもう一度お試しください。`;
          } else {
            message = `デバイス種類追加に失敗しました。\nしばらくしてからもう一度お試しください。`;
          }
          const alertDialogOptions: AlertDialogOption = {
            type: 'error',
            message: message,
          };
          this.dialog.open(AlertDialogComponent, {
            width: '600px',
            data: alertDialogOptions,
          });
        },
      });
    }
  }

  /**
   * デバイス種類を削除する
   * @param deviceTypeKey デバイス種類内部キー
   */
  public async deleteDeviceType(deviceTypeKey: string) {
    const deviceType = this.deviceTypeList.find(
      (dt) => dt.deviceTypeKey === deviceTypeKey,
    );
    if (deviceType) {
      const deviceTypeCount = deviceType.deviceTypeCount;
      if (deviceTypeCount && deviceTypeCount > 0) {
        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message: '紐づいているデバイスがあるため、削除できません',
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
        return;
      } else {
        const options: AlertDialogOption = {
          type: 'error',
          title: 'Confirm',
          message: `選択したデバイス種類を削除してよろしいですか？`,
          operation: '削除',
        };

        const result: boolean = await firstValueFrom(
          this.dialog
            .open(AlertDialogComponent, {
              width: '600px',
              data: options,
            })
            .afterClosed(),
        );

        if (!result) {
          return;
        }
      }
    }

    this.deviceTypeService.delete(deviceTypeKey).subscribe({
      next: () => {
        // デバイス種類一覧を再取得する
        this._initDeviceTypeList();
      },
      error: (error) => {
        let message = '';
        if (error.response.status === 409) {
          message = `デバイス種類削除に失敗しました。\n使用しているデバイスが存在します。`;
        } else {
          message = `デバイス種類削除に失敗しました。\nしばらくしてからもう一度お試しください。`;
        }
        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message: message,
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
      },
    });
  }

  private _initDeviceTypeList() {
    this.deviceTypeService.findAll().subscribe({
      next: (deviceTypeList) => {
        this.deviceTypeList = deviceTypeList;
      },
    });
  }
}
